export type WorkflowNodeKind = "trigger" | "action" | "condition" | "wait" | "ai";

export type FunnelNodeCategory =
  | "touchpoint"
  | "agent"
  | "trigger"
  | "routing"
  | "data"
  | "handoff"
  | "terminal";

export type FunnelNodeType =
  | "sms_send"
  | "inbound_sms_agent"
  | "ringless_voicemail"
  | "outbound_ai_voice_agent"
  | "inbound_ai_voice_agent"
  | "mailwizz_email_handoff"
  | "receive_event_webhook"
  | "wait"
  | "router_filter"
  | "external_event_webhook"
  | "contact_update"
  | "human_handoff"
  | "stop";

export type FunnelPort = {
  id: string;
  label: string;
  event?: string;
};

export type FunnelNodeRegistryEntry = {
  type: FunnelNodeType;
  version: number;
  label: string;
  category: FunnelNodeCategory;
  description: string;
  inputPorts: FunnelPort[];
  outputPorts: FunnelPort[];
  entitlementKey: string;
  executionHandlerKey: string;
  requiredConfig: string[];
  optionalConfig: string[];
  allowedAsFirstNode?: boolean;
  allowedAsFirstNodeByDefault?: boolean;
  allowsMultipleOutgoingEdgesByPort?: boolean;
  configUiSchema?: Record<string, unknown>;
};

export type FunnelNodeDefinition = {
  id: string;
  type: FunnelNodeType;
  label: string;
  config: Record<string, unknown>;
  layout: {
    x: number;
    y: number;
  };
  status?: "draft" | "valid" | "invalid" | "blocked";
};

export type FunnelEdgeDefinition = {
  id: string;
  sourceNodeId: string;
  sourcePortId: string;
  targetNodeId: string;
  targetPortId?: string;
  label?: string;
  condition?: Record<string, unknown>;
};

export type FunnelDefinition = {
  id: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  campaignId?: string;
  name: string;
  version: number;
  mode: "beginner" | "advanced";
  nodes: FunnelNodeDefinition[];
  edges: FunnelEdgeDefinition[];
  viewport: {
    zoom: number;
    x: number;
    y: number;
  };
  metadata: Record<string, unknown>;
};

export type FunnelStarterTemplateDefinition = {
  id: string;
  name: string;
  description?: string;
  useCase?: string;
  channelTags?: string[];
  requiredNodeTypes?: string[];
  availability?: string;
  definition: {
    nodes: FunnelNodeDefinition[];
    edges: FunnelEdgeDefinition[];
    viewport?: FunnelDefinition["viewport"];
    metadata?: Record<string, unknown>;
  };
};

export type FunnelValidationIssue = WorkflowValidationIssue & {
  edgeId?: string;
  nodeType?: FunnelNodeType;
};

export type FunnelValidationResult = {
  valid: boolean;
  issues: FunnelValidationIssue[];
};

export type FunnelStatus = "draft" | "published" | "archived";

export type FunnelDefinitionRecord = {
  id: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  campaignId?: string;
  name: string;
  status: FunnelStatus;
  activeVersionId?: string;
  activeVersion?: number;
  createdByUserId?: string;
  createdAt: string;
  updatedAt: string;
};

export type FunnelVersionRecord = {
  id: string;
  funnelId: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  version: number;
  definition: FunnelDefinition;
  status: FunnelStatus;
  validation: FunnelValidationResult;
  publishedAt?: string;
  createdAt: string;
};

export type FunnelExternalEventRoute = {
  routeId: string;
  funnelId: string;
  nodeId: string;
  eventName: string;
  webhookPath: string;
  token: string;
  createdAt: string;
};

export type FunnelExternalEventPayload = {
  eventName: string;
  contactId?: string;
  email?: string;
  phone?: string;
  campaignId?: string;
  touchpointId?: string;
  status?: string;
  externalEventId?: string;
  occurredAt: string;
  metadata: Record<string, unknown>;
};

export type FunnelSessionStatus =
  | "started"
  | "running"
  | "waiting"
  | "completed"
  | "failed"
  | "cancelled"
  | "expired";

export type FunnelSessionSource =
  | "campaign"
  | "manual"
  | "dry_run"
  | "receive_event_webhook"
  | "external_event"
  | "sms_reply"
  | "voice_callback";

export type FunnelSessionRecord = {
  id: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  campaignId?: string;
  contactId?: string;
  funnelId?: string;
  funnelVersionId?: string;
  executionId?: string;
  status: FunnelSessionStatus;
  source: FunnelSessionSource | string;
  currentNodeKey?: string;
  startedAt: string;
  completedAt?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown>;
};

export type FunnelSessionEventRecord = {
  id: string;
  sessionId: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  eventType: string;
  status?: string;
  payload: Record<string, unknown>;
  occurredAt: string;
};

export type FunnelNodeEventRecord = {
  id: string;
  sessionId: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  nodeKey: string;
  nodeType: string;
  eventType: string;
  status: string;
  inputPayload: Record<string, unknown>;
  outputPayload: Record<string, unknown>;
  redaction: Record<string, unknown>;
  errorCode?: string;
  errorMessage?: string;
  providerEventId?: string;
  occurredAt: string;
};

export type FunnelWaitStateRecord = {
  id: string;
  sessionId: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  sourceNodeKey: string;
  awaitedEventTypes: string[];
  status: "waiting" | "resolved" | "expired" | "cancelled";
  nextRouteHints: Record<string, unknown>;
  expiresAt?: string;
  resolvedAt?: string;
  resolvedEventType?: string;
  resolvedEventId?: string;
  createdAt: string;
  updatedAt: string;
};

export type WorkflowNodeDefinition = {
  id: string;
  kind: WorkflowNodeKind;
  name: string;
  config: Record<string, unknown>;
};

export type WorkflowConnectionDefinition = {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  label?: string;
  condition?: Record<string, unknown>;
};

export type WorkflowDefinition = {
  id: string;
  tenantId: string;
  name: string;
  version: number;
  nodes: WorkflowNodeDefinition[];
  connections?: WorkflowConnectionDefinition[];
};

export const funnelNodeRegistry = [
  {
    type: "sms_send",
    version: 1,
    label: "SMS Send",
    category: "touchpoint",
    description: "Send an outbound SMS using campaign sender routing and personalization macros.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "sent", label: "Sent", event: "sms.sent" },
      { id: "failed", label: "Failed", event: "sms.failed" },
      { id: "reply", label: "Reply", event: "sms.reply" },
    ],
    entitlementKey: "funnels.node.sms_send",
    executionHandlerKey: "communications.sms.send",
    requiredConfig: ["body"],
    optionalConfig: ["senderRoute", "trackingLinks", "macros"],
    allowedAsFirstNode: true,
    allowedAsFirstNodeByDefault: true,
  },
  {
    type: "inbound_sms_agent",
    version: 1,
    label: "Inbound SMS Agent",
    category: "agent",
    description: "Handle inbound SMS replies with AI, human, or hybrid routing.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "responded", label: "Responded", event: "agent.sms.responded" },
      { id: "escalated", label: "Escalated", event: "agent.sms.escalated" },
      { id: "opt_out", label: "Opt-out", event: "contact.opt_out" },
    ],
    entitlementKey: "funnels.node.inbound_sms_agent",
    executionHandlerKey: "ai.agent.sms.inbound",
    requiredConfig: [],
    optionalConfig: [
      "knowledgebaseIds",
      "instructions",
      "maxTurns",
      "mode",
      "trackingLinks",
      "macros",
    ],
  },
  {
    type: "ringless_voicemail",
    version: 1,
    label: "Ringless Voicemail",
    category: "touchpoint",
    description: "Submit a ringless voicemail drop and branch on delivery and response outcomes.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "submitted", label: "Submitted", event: "rvd.submitted" },
      { id: "delivered", label: "Delivered", event: "rvd.delivered" },
      { id: "failed", label: "Failed", event: "rvd.failed" },
      { id: "response", label: "Response", event: "rvd.response" },
    ],
    entitlementKey: "funnels.node.ringless_voicemail",
    executionHandlerKey: "voicemail.ringless.submit",
    requiredConfig: ["templateId"],
    optionalConfig: ["providerRoute", "voiceId", "script"],
    allowedAsFirstNode: true,
    allowedAsFirstNodeByDefault: true,
  },
  {
    type: "outbound_ai_voice_agent",
    version: 1,
    label: "Outbound AI Voice",
    category: "agent",
    description: "Start an outbound AI voice call with governed memory and knowledgebase context.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "answered", label: "Answered", event: "voice.answered" },
      { id: "voicemail", label: "Voicemail", event: "voice.voicemail" },
      { id: "failed", label: "Failed", event: "voice.failed" },
      { id: "sms_requested", label: "SMS requested", event: "voice.sms_requested" },
    ],
    entitlementKey: "funnels.node.outbound_ai_voice_agent",
    executionHandlerKey: "ai.agent.voice.outbound",
    requiredConfig: ["script"],
    optionalConfig: [
      "knowledgebaseIds",
      "instructions",
      "voiceId",
      "senderRoute",
      "trackingLinks",
      "macros",
    ],
    allowedAsFirstNode: true,
    allowedAsFirstNodeByDefault: true,
  },
  {
    type: "inbound_ai_voice_agent",
    version: 1,
    label: "Inbound AI Voice",
    category: "agent",
    description:
      "Handle inbound calls and callbacks with contact memory and knowledgebase context.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "resolved", label: "Resolved", event: "voice.inbound.resolved" },
      { id: "transferred", label: "Transferred", event: "voice.inbound.transferred" },
      { id: "sms_requested", label: "SMS requested", event: "voice.sms_requested" },
    ],
    entitlementKey: "funnels.node.inbound_ai_voice_agent",
    executionHandlerKey: "ai.agent.voice.inbound",
    requiredConfig: [],
    optionalConfig: [
      "knowledgebaseIds",
      "instructions",
      "transferRules",
      "smsToolEnabled",
      "trackingLinks",
      "macros",
    ],
  },
  {
    type: "mailwizz_email_handoff",
    version: 1,
    label: "MailWizz Email",
    category: "touchpoint",
    description: "Add or update a subscriber in MailWizz and set list custom fields.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "synced", label: "Synced", event: "mailwizz.synced" },
      { id: "failed", label: "Failed", event: "mailwizz.failed" },
    ],
    entitlementKey: "funnels.node.mailwizz_email_handoff",
    executionHandlerKey: "mailwizz.subscriber.upsert",
    requiredConfig: ["listId"],
    optionalConfig: ["customFields", "campaignReference", "trackingLinks", "macros"],
  },
  {
    type: "receive_event_webhook",
    version: 1,
    label: "Receive Event / Webhook",
    category: "trigger",
    description: "Receive an external webhook event and start a funnel session for a contact.",
    inputPorts: [],
    outputPorts: [
      { id: "received", label: "Received", event: "external_event.received" },
      { id: "unmatched", label: "Unmatched", event: "external_event.unmatched" },
    ],
    entitlementKey: "funnels.node.receive_event_webhook",
    executionHandlerKey: "webhooks.receive_event.trigger",
    requiredConfig: ["eventName"],
    optionalConfig: ["contactMatchKey", "payloadSchema", "campaignBinding"],
    allowedAsFirstNode: true,
    allowedAsFirstNodeByDefault: true,
  },
  {
    type: "wait",
    version: 1,
    label: "Wait",
    category: "routing",
    description:
      "Pause this funnel session until one of the allowed reply or provider events resumes it.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "sms_reply", label: "SMS reply", event: "sms.reply" },
      { id: "voice_callback", label: "Voice callback", event: "voice.callback" },
      { id: "external_event", label: "External event", event: "external_event.received" },
      { id: "link_click", label: "Link click", event: "tracking_link.clicked" },
      { id: "conversion", label: "Conversion", event: "conversion.received" },
      { id: "provider_status", label: "Provider status", event: "provider.status" },
      { id: "timeout", label: "Timeout", event: "wait.timeout" },
    ],
    entitlementKey: "funnels.node.wait",
    executionHandlerKey: "workflow.wait",
    requiredConfig: [],
    optionalConfig: ["eventTypes", "fallbackMaxWait", "businessHoursOnly"],
    allowsMultipleOutgoingEdgesByPort: false,
  },
  {
    type: "router_filter",
    version: 1,
    label: "Router / Filter",
    category: "routing",
    description:
      "Evaluate event payloads, contact fields, inactivity windows, or custom conditions.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "matched", label: "Matched", event: "route.matched" },
      { id: "not_matched", label: "Not matched", event: "route.not_matched" },
    ],
    entitlementKey: "funnels.node.router_filter",
    executionHandlerKey: "workflow.router.filter",
    requiredConfig: ["conditions"],
    optionalConfig: ["matchMode", "groups", "caseSensitive", "strictTypes", "advancedJson"],
  },
  {
    type: "external_event_webhook",
    version: 1,
    label: "Wait for External Event",
    category: "routing",
    description: "Wait inside a running funnel for an external conversion or status event.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "received", label: "Received", event: "external_event.received" },
      { id: "timeout", label: "Timeout", event: "external_event.timeout" },
    ],
    entitlementKey: "funnels.node.external_event_webhook",
    executionHandlerKey: "webhooks.external_event.wait",
    requiredConfig: ["eventName"],
    optionalConfig: ["contactMatchKey", "timeout"],
  },
  {
    type: "contact_update",
    version: 1,
    label: "Contact Update",
    category: "data",
    description: "Update tags, status, preferred channels, custom fields, or conversion flags.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [{ id: "updated", label: "Updated", event: "contact.updated" }],
    entitlementKey: "funnels.node.contact_update",
    executionHandlerKey: "crm.contact.update",
    requiredConfig: ["updates"],
    optionalConfig: ["tags", "customFields", "preferredChannel"],
  },
  {
    type: "human_handoff",
    version: 1,
    label: "Human Handoff",
    category: "handoff",
    description: "Create an inbox task for human review with assignment and SLA metadata.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [
      { id: "created", label: "Created", event: "handoff.created" },
      { id: "resolved", label: "Resolved", event: "handoff.resolved" },
    ],
    entitlementKey: "funnels.node.human_handoff",
    executionHandlerKey: "inbox.handoff.create",
    requiredConfig: ["reason"],
    optionalConfig: ["priority", "slaMinutes", "assignmentRule"],
  },
  {
    type: "stop",
    version: 1,
    label: "Stop",
    category: "terminal",
    description: "Terminate the current funnel path with an explicit outcome.",
    inputPorts: [{ id: "in", label: "In" }],
    outputPorts: [],
    entitlementKey: "funnels.node.stop",
    executionHandlerKey: "workflow.stop",
    requiredConfig: ["outcome"],
    optionalConfig: ["notes"],
  },
] as const satisfies readonly FunnelNodeRegistryEntry[];

const funnelNodeRegistryByType = new Map<FunnelNodeType, FunnelNodeRegistryEntry>(
  funnelNodeRegistry.map((entry) => [entry.type, entry]),
);

export type FunnelProviderConnectionType = "twilio" | "smtp" | "openai";

const providerConnectionTypeByNode = new Map<FunnelNodeType, FunnelProviderConnectionType>([
  ["sms_send", "twilio"],
  ["inbound_sms_agent", "openai"],
  ["outbound_ai_voice_agent", "openai"],
  ["inbound_ai_voice_agent", "openai"],
  ["mailwizz_email_handoff", "smtp"],
]);

export function providerConnectionTypeForFunnelNode(
  nodeType: FunnelNodeType,
): FunnelProviderConnectionType | undefined {
  return providerConnectionTypeByNode.get(nodeType);
}

const waitEventsBySourceEvent = new Map<string, readonly string[]>([
  ["sms.sent", ["sms.reply", "tracking_link.clicked", "provider.status"]],
  ["sms.reply", ["sms.reply"]],
  ["rvd.submitted", ["sms.reply", "voice.callback", "external_event.received", "provider.status"]],
  ["rvd.delivered", ["sms.reply", "voice.callback", "external_event.received", "provider.status"]],
  ["rvd.response", ["sms.reply", "voice.callback", "external_event.received"]],
  ["voice.answered", ["sms.reply", "voice.callback", "external_event.received", "provider.status"]],
  ["voice.voicemail", ["sms.reply", "voice.callback", "provider.status"]],
  ["voice.sms_requested", ["sms.reply", "tracking_link.clicked", "conversion.received"]],
  ["external_event.received", ["external_event.received", "conversion.received"]],
  ["mailwizz.synced", ["external_event.received", "conversion.received", "tracking_link.clicked"]],
]);

const waitConsumersByEvent = new Map<string, readonly FunnelNodeType[]>([
  ["sms.reply", ["inbound_sms_agent", "router_filter", "contact_update", "human_handoff", "stop"]],
  [
    "voice.callback",
    ["inbound_ai_voice_agent", "router_filter", "contact_update", "human_handoff", "stop"],
  ],
  ["external_event.received", ["router_filter", "contact_update", "human_handoff", "stop"]],
  ["tracking_link.clicked", ["router_filter", "contact_update", "human_handoff", "stop"]],
  ["conversion.received", ["router_filter", "contact_update", "human_handoff", "stop"]],
  ["provider.status", ["router_filter", "contact_update", "human_handoff", "stop"]],
  ["wait.timeout", ["router_filter", "contact_update", "human_handoff", "stop"]],
]);

const durationMsByOption = new Map<string, number>([
  ["1d", 24 * 60 * 60 * 1000],
  ["2d", 2 * 24 * 60 * 60 * 1000],
  ["3d", 3 * 24 * 60 * 60 * 1000],
  ["4d", 4 * 24 * 60 * 60 * 1000],
  ["5d", 5 * 24 * 60 * 60 * 1000],
  ["6d", 6 * 24 * 60 * 60 * 1000],
  ["1w", 7 * 24 * 60 * 60 * 1000],
  ["2w", 14 * 24 * 60 * 60 * 1000],
  ["3w", 21 * 24 * 60 * 60 * 1000],
  ["1m", 31 * 24 * 60 * 60 * 1000],
]);

const allowedContactUpdateFields = new Set([
  "firstName",
  "lastName",
  "displayName",
  "company",
  "phone",
  "email",
  "status",
  "leadScore",
  "tags",
  "preferredChannel",
  "conversionFlag",
  "customField",
]);

const allowedContactStatusValues = new Set([
  "new",
  "active",
  "qualified",
  "do_not_contact",
  "archived",
]);

function parseDurationMs(value: unknown): number | undefined {
  if (typeof value !== "string" || !value.trim()) {
    return undefined;
  }

  const normalized = value.trim();
  const optionMs = durationMsByOption.get(normalized);

  if (optionMs !== undefined) {
    return optionMs;
  }

  const isoDayMatch = /^P(\d+)D$/i.exec(normalized);

  if (isoDayMatch) {
    return Number(isoDayMatch[1]) * 24 * 60 * 60 * 1000;
  }

  const isoHourMatch = /^PT(\d+)H$/i.exec(normalized);

  if (isoHourMatch) {
    return Number(isoHourMatch[1]) * 60 * 60 * 1000;
  }

  return undefined;
}

function campaignDurationMs(definition: FunnelDefinition): number | undefined {
  return parseDurationMs(
    definition.metadata["campaignDuration"] ??
      definition.metadata["campaign_duration"] ??
      definition.metadata["activeDuration"],
  );
}

export type WorkflowCanvasNode = WorkflowNodeDefinition & {
  position: {
    x: number;
    y: number;
  };
  status?: WorkflowNodeExecutionStatus;
};

export type WorkflowCanvasDefinition = {
  workflowId: string;
  tenantId: string;
  name: string;
  mode: "beginner" | "advanced";
  nodes: WorkflowCanvasNode[];
  connections: WorkflowConnectionDefinition[];
  viewport: {
    zoom: number;
    x: number;
    y: number;
  };
};

export type WorkflowValidationSeverity = "error" | "warning" | "info";

export type WorkflowValidationIssue = {
  code: string;
  severity: WorkflowValidationSeverity;
  message: string;
  nodeId?: string;
};

export type WorkflowValidationResult = {
  valid: boolean;
  issues: WorkflowValidationIssue[];
};

export type WorkflowStatus = "draft" | "published" | "archived";

export type WorkflowPersistenceRecord = {
  id: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  name: string;
  status: WorkflowStatus;
  activeVersion?: number;
  createdByUserId?: string;
  createdAt: string;
  updatedAt: string;
};

export type WorkflowVersionRecord = {
  id: string;
  workflowId: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  version: number;
  definition: WorkflowDefinition;
  status: WorkflowStatus;
  publishedAt?: string;
  createdAt: string;
};

export type WorkflowNodePersistenceRecord = {
  id: string;
  workflowVersionId: string;
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  nodeKey: string;
  kind: WorkflowNodeKind;
  name: string;
  config: Record<string, unknown>;
  position: {
    x: number;
    y: number;
  };
};

export type WorkflowTriggerType =
  | "manual"
  | "contact.created"
  | "contact.updated"
  | "webhook.received"
  | "schedule.elapsed";

export const temporalTaskQueues = {
  workflowExecution: "zeary-workflow-execution",
  aiRuntime: "zeary-ai-runtime",
  communications: "zeary-communications",
  webhooks: "zeary-webhooks",
} as const;

export type TemporalTaskQueue = (typeof temporalTaskQueues)[keyof typeof temporalTaskQueues];

export type WorkflowRetryPolicy = {
  initialIntervalMs: number;
  maximumIntervalMs: number;
  backoffCoefficient: number;
  maximumAttempts: number;
  nonRetryableErrorTypes: string[];
};

export const workflowRetryPolicies = {
  standard: {
    initialIntervalMs: 1_000,
    maximumIntervalMs: 60_000,
    backoffCoefficient: 2,
    maximumAttempts: 5,
    nonRetryableErrorTypes: ["TenantScopeViolation", "PermissionDenied"],
  },
  realtime: {
    initialIntervalMs: 250,
    maximumIntervalMs: 5_000,
    backoffCoefficient: 2,
    maximumAttempts: 3,
    nonRetryableErrorTypes: ["TenantScopeViolation", "PermissionDenied"],
  },
} as const satisfies Record<string, WorkflowRetryPolicy>;

export type WorkflowExecutionPlan = {
  workflowId: string;
  workflowVersion: number;
  tenantId: string;
  taskQueue: TemporalTaskQueue;
  retryPolicy: WorkflowRetryPolicy;
  searchAttributes: Record<string, string>;
};

export type TemporalWorkflowOptions = {
  workflowId: string;
  taskQueue: TemporalTaskQueue;
  retry: WorkflowRetryPolicy;
  memo: Record<string, unknown>;
  searchAttributes: Record<string, string>;
};

export type WorkflowRunRequest = {
  executionId: string;
  workflowId: string;
  workflowVersion: number;
  tenantId: string;
  triggerType: WorkflowTriggerType;
  traceId: string;
  input: Record<string, unknown>;
  temporalOptions: TemporalWorkflowOptions;
  snapshot: WorkflowStateSnapshot;
};

export type WorkflowWorkerConfig = {
  namespace: string;
  taskQueue: TemporalTaskQueue;
  maxConcurrentWorkflowTaskExecutions: number;
  maxConcurrentActivityTaskExecutions: number;
};

export type WorkflowStateSnapshot = {
  executionId: string;
  workflowId: string;
  tenantId: string;
  status: "scheduled" | "running" | "completed" | "failed" | "cancelled";
  currentNodeId?: string;
  attempt: number;
  updatedAt: string;
};

export type WorkflowNodeExecutionStatus =
  | "inactive"
  | "scheduled"
  | "running"
  | "completed"
  | "failed"
  | "waiting";

export type WorkflowExecutionLogEntry = {
  id: string;
  executionId: string;
  workflowId: string;
  tenantId: string;
  nodeId?: string;
  level: "debug" | "info" | "warn" | "error";
  message: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  error?: {
    code: string;
    message: string;
  };
  attempt: number;
  occurredAt: string;
};

export type WorkflowReplayRequest = {
  id: string;
  executionId: string;
  workflowId: string;
  tenantId: string;
  requestedBy: string;
  mode: "full" | "from_node";
  fromNodeId?: string;
  reason: string;
  createdAt: string;
};

export type WorkflowRetryRequest = {
  id: string;
  executionId: string;
  workflowId: string;
  tenantId: string;
  nodeId: string;
  requestedBy: string;
  reason: string;
  createdAt: string;
};

export function assertDeterministicWorkflowDefinition(definition: WorkflowDefinition): void {
  if (!definition.tenantId) {
    throw new Error("Workflow definitions require tenant context.");
  }

  if (definition.version < 1) {
    throw new Error("Workflow definitions require a positive version.");
  }

  const nodeIds = new Set<string>();

  for (const node of definition.nodes) {
    if (nodeIds.has(node.id)) {
      throw new Error(`Duplicate workflow node id: ${node.id}`);
    }

    nodeIds.add(node.id);
  }

  if (definition.nodes.length === 0) {
    throw new Error("Workflow definitions require at least one node.");
  }

  for (const connection of definition.connections ?? []) {
    if (!nodeIds.has(connection.sourceNodeId) || !nodeIds.has(connection.targetNodeId)) {
      throw new Error(`Workflow connection references an unknown node: ${connection.id}`);
    }
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

function createId(prefix: string): string {
  return `${prefix}_${globalThis.crypto.randomUUID()}`;
}

function assertTenantInput(input: { tenantId: string; organizationId: string }): void {
  if (!input.tenantId || !input.organizationId) {
    throw new Error("Workflow persistence requires tenant and organization context.");
  }
}

export function createWorkflowExecutionPlan(input: {
  definition: WorkflowDefinition;
  taskQueue?: TemporalTaskQueue;
  retryPolicy?: WorkflowRetryPolicy;
}): WorkflowExecutionPlan {
  assertDeterministicWorkflowDefinition(input.definition);

  return {
    workflowId: input.definition.id,
    workflowVersion: input.definition.version,
    tenantId: input.definition.tenantId,
    taskQueue: input.taskQueue ?? temporalTaskQueues.workflowExecution,
    retryPolicy: input.retryPolicy ?? workflowRetryPolicies.standard,
    searchAttributes: {
      TenantId: input.definition.tenantId,
      WorkflowId: input.definition.id,
      WorkflowVersion: String(input.definition.version),
    },
  };
}

export function createTemporalWorkflowOptions(
  plan: WorkflowExecutionPlan,
): TemporalWorkflowOptions {
  return {
    workflowId: `${plan.tenantId}.${plan.workflowId}.v${plan.workflowVersion}`,
    taskQueue: plan.taskQueue,
    retry: plan.retryPolicy,
    memo: {
      tenantId: plan.tenantId,
      workflowId: plan.workflowId,
      workflowVersion: plan.workflowVersion,
    },
    searchAttributes: plan.searchAttributes,
  };
}

export function createWorkflowPersistenceRecord(input: {
  definition: WorkflowDefinition;
  organizationId: string;
  agencyId?: string;
  createdByUserId?: string;
  status?: WorkflowStatus;
  createdAt?: string;
  updatedAt?: string;
}): WorkflowPersistenceRecord {
  assertDeterministicWorkflowDefinition(input.definition);
  assertTenantInput({
    tenantId: input.definition.tenantId,
    organizationId: input.organizationId,
  });

  const createdAt = input.createdAt ?? nowIso();

  return {
    id: input.definition.id,
    tenantId: input.definition.tenantId,
    organizationId: input.organizationId,
    agencyId: input.agencyId,
    name: input.definition.name,
    status: input.status ?? "draft",
    activeVersion: input.status === "published" ? input.definition.version : undefined,
    createdByUserId: input.createdByUserId,
    createdAt,
    updatedAt: input.updatedAt ?? createdAt,
  };
}

export function createWorkflowVersionRecord(input: {
  definition: WorkflowDefinition;
  organizationId: string;
  agencyId?: string;
  status?: WorkflowStatus;
  id?: string;
  createdAt?: string;
  publishedAt?: string;
}): WorkflowVersionRecord {
  assertDeterministicWorkflowDefinition(input.definition);
  assertTenantInput({
    tenantId: input.definition.tenantId,
    organizationId: input.organizationId,
  });

  return {
    id: input.id ?? createId("workflow_version"),
    workflowId: input.definition.id,
    tenantId: input.definition.tenantId,
    organizationId: input.organizationId,
    agencyId: input.agencyId,
    version: input.definition.version,
    definition: input.definition,
    status: input.status ?? "draft",
    publishedAt: input.publishedAt,
    createdAt: input.createdAt ?? nowIso(),
  };
}

export function createWorkflowNodePersistenceRecords(input: {
  version: WorkflowVersionRecord;
  positions?: Record<string, { x: number; y: number }>;
}): WorkflowNodePersistenceRecord[] {
  return input.version.definition.nodes.map((node, index) => ({
    id: createId("workflow_node"),
    workflowVersionId: input.version.id,
    tenantId: input.version.tenantId,
    organizationId: input.version.organizationId,
    agencyId: input.version.agencyId,
    nodeKey: node.id,
    kind: node.kind,
    name: node.name,
    config: node.config,
    position: input.positions?.[node.id] ?? {
      x: index * 220,
      y: 0,
    },
  }));
}

export function createWorkflowCanvasDefinition(input: {
  definition: WorkflowDefinition;
  mode?: WorkflowCanvasDefinition["mode"];
  positions?: Record<string, { x: number; y: number }>;
  viewport?: WorkflowCanvasDefinition["viewport"];
}): WorkflowCanvasDefinition {
  assertDeterministicWorkflowDefinition(input.definition);

  return {
    workflowId: input.definition.id,
    tenantId: input.definition.tenantId,
    name: input.definition.name,
    mode: input.mode ?? "beginner",
    nodes: input.definition.nodes.map((node, index) => ({
      ...node,
      position: input.positions?.[node.id] ?? {
        x: index * 240,
        y: index % 2 === 0 ? 0 : 140,
      },
      status: "inactive",
    })),
    connections: input.definition.connections ?? [],
    viewport: input.viewport ?? {
      zoom: 1,
      x: 0,
      y: 0,
    },
  };
}

export function validateWorkflowDefinition(
  definition: WorkflowDefinition,
): WorkflowValidationResult {
  const issues: WorkflowValidationIssue[] = [];

  try {
    assertDeterministicWorkflowDefinition(definition);
  } catch (error) {
    issues.push({
      code: "WORKFLOW_DEFINITION_INVALID",
      severity: "error",
      message: error instanceof Error ? error.message : "Workflow definition is invalid.",
    });
  }

  const triggerCount = definition.nodes.filter((node) => node.kind === "trigger").length;

  if (triggerCount === 0) {
    issues.push({
      code: "WORKFLOW_TRIGGER_MISSING",
      severity: "error",
      message: "Workflow requires at least one trigger node.",
    });
  }

  const targetNodeIds = new Set(
    (definition.connections ?? []).map((connection) => connection.targetNodeId),
  );

  for (const node of definition.nodes) {
    if (node.kind !== "trigger" && !targetNodeIds.has(node.id)) {
      issues.push({
        code: "WORKFLOW_NODE_UNREACHABLE",
        severity: "warning",
        message: "Node is not reachable from a configured connection.",
        nodeId: node.id,
      });
    }
  }

  return {
    valid: issues.every((issue) => issue.severity !== "error"),
    issues,
  };
}

export function createWorkflowExecutionLogEntry(input: {
  executionId: string;
  workflowId: string;
  tenantId: string;
  nodeId?: string;
  level?: WorkflowExecutionLogEntry["level"];
  message: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  error?: WorkflowExecutionLogEntry["error"];
  attempt?: number;
  occurredAt?: string;
}): WorkflowExecutionLogEntry {
  if (!input.tenantId || !input.executionId || !input.workflowId) {
    throw new Error("Workflow logs require tenant, workflow, and execution context.");
  }

  return {
    id: createId("workflow_log"),
    executionId: input.executionId,
    workflowId: input.workflowId,
    tenantId: input.tenantId,
    nodeId: input.nodeId,
    level: input.level ?? "info",
    message: input.message,
    input: input.input,
    output: input.output,
    error: input.error,
    attempt: input.attempt ?? 1,
    occurredAt: input.occurredAt ?? nowIso(),
  };
}

export function createWorkflowReplayRequest(input: {
  executionId: string;
  workflowId: string;
  tenantId: string;
  requestedBy: string;
  reason: string;
  mode?: WorkflowReplayRequest["mode"];
  fromNodeId?: string;
  createdAt?: string;
}): WorkflowReplayRequest {
  if (input.mode === "from_node" && !input.fromNodeId) {
    throw new Error("Replay from node requires a source node id.");
  }

  return {
    id: createId("workflow_replay"),
    executionId: input.executionId,
    workflowId: input.workflowId,
    tenantId: input.tenantId,
    requestedBy: input.requestedBy,
    mode: input.mode ?? "full",
    fromNodeId: input.fromNodeId,
    reason: input.reason,
    createdAt: input.createdAt ?? nowIso(),
  };
}

export function createWorkflowRetryRequest(input: {
  executionId: string;
  workflowId: string;
  tenantId: string;
  nodeId: string;
  requestedBy: string;
  reason: string;
  createdAt?: string;
}): WorkflowRetryRequest {
  if (!input.nodeId) {
    throw new Error("Workflow retry requests require a node id.");
  }

  return {
    id: createId("workflow_retry"),
    executionId: input.executionId,
    workflowId: input.workflowId,
    tenantId: input.tenantId,
    nodeId: input.nodeId,
    requestedBy: input.requestedBy,
    reason: input.reason,
    createdAt: input.createdAt ?? nowIso(),
  };
}

export function createWorkflowRunRequest(input: {
  definition: WorkflowDefinition;
  triggerType: WorkflowTriggerType;
  traceId: string;
  input?: Record<string, unknown>;
  taskQueue?: TemporalTaskQueue;
  retryPolicy?: WorkflowRetryPolicy;
  executionId?: string;
}): WorkflowRunRequest {
  const plan = createWorkflowExecutionPlan({
    definition: input.definition,
    taskQueue: input.taskQueue,
    retryPolicy: input.retryPolicy,
  });
  const executionId = input.executionId ?? createId("workflow_execution");

  return {
    executionId,
    workflowId: input.definition.id,
    workflowVersion: input.definition.version,
    tenantId: input.definition.tenantId,
    triggerType: input.triggerType,
    traceId: input.traceId,
    input: input.input ?? {},
    temporalOptions: createTemporalWorkflowOptions(plan),
    snapshot: createWorkflowStateSnapshot({
      executionId,
      workflowId: input.definition.id,
      tenantId: input.definition.tenantId,
      status: "scheduled",
      attempt: 1,
    }),
  };
}

export function createWorkflowWorkerConfig(input: {
  namespace?: string;
  taskQueue: TemporalTaskQueue;
  maxConcurrentWorkflowTaskExecutions?: number;
  maxConcurrentActivityTaskExecutions?: number;
}): WorkflowWorkerConfig {
  return {
    namespace: input.namespace ?? "default",
    taskQueue: input.taskQueue,
    maxConcurrentWorkflowTaskExecutions: input.maxConcurrentWorkflowTaskExecutions ?? 50,
    maxConcurrentActivityTaskExecutions: input.maxConcurrentActivityTaskExecutions ?? 100,
  };
}

export function createWorkflowStateSnapshot(
  input: Omit<WorkflowStateSnapshot, "updatedAt"> & {
    updatedAt?: string;
  },
): WorkflowStateSnapshot {
  return {
    ...input,
    updatedAt: input.updatedAt ?? new Date().toISOString(),
  };
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function hasRequiredConfig(config: Record<string, unknown>, key: string): boolean {
  const value = config[key];

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === "object") {
    return Object.keys(value).length > 0;
  }

  return isNonEmptyString(value) || typeof value === "number" || typeof value === "boolean";
}

function funnelNodeWorkflowKind(type: FunnelNodeType): WorkflowNodeKind {
  const registryEntry = funnelNodeRegistryByType.get(type);

  if (registryEntry?.category === "agent") {
    return "ai";
  }

  if (type === "wait") {
    return "wait";
  }

  if (
    type === "router_filter" ||
    type === "external_event_webhook" ||
    registryEntry?.category === "routing"
  ) {
    return "condition";
  }

  return "action";
}

function reachableFunnelNodeIds(definition: FunnelDefinition): Set<string> {
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, string[]>();

  for (const node of definition.nodes) {
    incoming.set(node.id, 0);
    outgoing.set(node.id, []);
  }

  for (const edge of definition.edges) {
    incoming.set(edge.targetNodeId, (incoming.get(edge.targetNodeId) ?? 0) + 1);
    outgoing.get(edge.sourceNodeId)?.push(edge.targetNodeId);
  }

  const roots = definition.nodes.filter((node) => (incoming.get(node.id) ?? 0) === 0);
  const queue =
    roots.length > 0 ? roots.map((node) => node.id) : [definition.nodes[0]?.id].filter(Boolean);
  const reachable = new Set<string>();

  while (queue.length > 0) {
    const nodeId = queue.shift();

    if (!nodeId || reachable.has(nodeId)) {
      continue;
    }

    reachable.add(nodeId);

    for (const targetNodeId of outgoing.get(nodeId) ?? []) {
      queue.push(targetNodeId);
    }
  }

  return reachable;
}

function funnelHasCycle(definition: FunnelDefinition): boolean {
  const outgoing = new Map<string, string[]>();

  for (const node of definition.nodes) {
    outgoing.set(node.id, []);
  }

  for (const edge of definition.edges) {
    outgoing.get(edge.sourceNodeId)?.push(edge.targetNodeId);
  }

  const visiting = new Set<string>();
  const visited = new Set<string>();

  function visit(nodeId: string): boolean {
    if (visiting.has(nodeId)) {
      return true;
    }

    if (visited.has(nodeId)) {
      return false;
    }

    visiting.add(nodeId);

    for (const nextNodeId of outgoing.get(nodeId) ?? []) {
      if (visit(nextNodeId)) {
        return true;
      }
    }

    visiting.delete(nodeId);
    visited.add(nodeId);

    return false;
  }

  return definition.nodes.some((node) => visit(node.id));
}

export function createStarterFunnelDefinition(input: {
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  campaignId?: string;
  id?: string;
  name?: string;
}): FunnelDefinition {
  const funnelId = input.id ?? createId("funnel");

  return {
    id: funnelId,
    tenantId: input.tenantId,
    organizationId: input.organizationId,
    agencyId: input.agencyId,
    campaignId: input.campaignId,
    name: input.name ?? "First campaign funnel",
    version: 1,
    mode: "beginner",
    nodes: [
      {
        id: "sms_intro",
        type: "sms_send",
        label: "Intro SMS",
        config: {
          body: "Hi [first_name], this is [organization_name]. Reply with any questions and we can help.",
        },
        layout: { x: 80, y: 120 },
      },
      {
        id: "wait_for_reply",
        type: "wait",
        label: "Wait for reply",
        config: {
          eventTypes: ["sms.reply"],
        },
        layout: { x: 360, y: 120 },
      },
      {
        id: "reply_router",
        type: "router_filter",
        label: "Reply or no reply",
        config: {
          matchMode: "any",
          conditions: [
            {
              left: { source: "event_payload", path: "eventName" },
              dataType: "string",
              operator: "equals",
              right: "sms.reply",
            },
          ],
          caseSensitive: false,
          strictTypes: false,
        },
        layout: { x: 640, y: 120 },
      },
      {
        id: "sms_agent",
        type: "inbound_sms_agent",
        label: "SMS Agent",
        config: {
          mode: "hybrid",
          knowledgebaseIds: [],
          instructions: "Keep replies concise and escalate uncertain answers.",
          maxTurns: 4,
        },
        layout: { x: 920, y: 40 },
      },
      {
        id: "finish",
        type: "stop",
        label: "End",
        config: {
          outcome: "completed",
        },
        layout: { x: 920, y: 220 },
      },
    ],
    edges: [
      {
        id: "edge_sms_wait",
        sourceNodeId: "sms_intro",
        sourcePortId: "sent",
        targetNodeId: "wait_for_reply",
        targetPortId: "in",
        label: "Sent",
      },
      {
        id: "edge_wait_router",
        sourceNodeId: "wait_for_reply",
        sourcePortId: "sms_reply",
        targetNodeId: "reply_router",
        targetPortId: "in",
        label: "SMS reply",
      },
      {
        id: "edge_router_agent",
        sourceNodeId: "reply_router",
        sourcePortId: "matched",
        targetNodeId: "sms_agent",
        targetPortId: "in",
        label: "Reply",
      },
      {
        id: "edge_router_end",
        sourceNodeId: "reply_router",
        sourcePortId: "not_matched",
        targetNodeId: "finish",
        targetPortId: "in",
        label: "No reply",
      },
      {
        id: "edge_agent_end",
        sourceNodeId: "sms_agent",
        sourcePortId: "responded",
        targetNodeId: "finish",
        targetPortId: "in",
        label: "Resolved",
      },
    ],
    viewport: { zoom: 1, x: 0, y: 0 },
    metadata: {
      template: "sms_intro_reply_agent",
      followUpsConfigurable: true,
    },
  };
}

export function createFunnelDefinitionFromTemplate(input: {
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  campaignId?: string;
  id?: string;
  name?: string;
  templateId?: string;
  template?: FunnelStarterTemplateDefinition;
}): FunnelDefinition {
  if (input.template) {
    const funnelId = input.id ?? createId("funnel");
    const template = input.template;

    return {
      id: funnelId,
      tenantId: input.tenantId,
      organizationId: input.organizationId,
      agencyId: input.agencyId,
      campaignId: input.campaignId,
      name: input.name ?? template.name,
      version: 1,
      mode: "beginner",
      nodes: template.definition.nodes.map((node) => ({
        ...node,
        config: { ...node.config },
        layout: { ...node.layout },
      })),
      edges: template.definition.edges.map((edge) => ({ ...edge })),
      viewport: template.definition.viewport ?? { zoom: 1, x: 0, y: 0 },
      metadata: {
        ...template.definition.metadata,
        template: template.id,
      },
    };
  }

  return createStarterFunnelDefinition(input);
}

export function validateFunnelDefinition(
  definition: FunnelDefinition,
  options: {
    maxNodes?: number;
    enabledNodeTypes?: readonly string[];
    allowedFirstNodeTypes?: readonly string[];
  } = {},
): FunnelValidationResult {
  const issues: FunnelValidationIssue[] = [];
  const maxNodes = options.maxNodes ?? 25;
  const nodeIds = new Set<string>();
  const enabledTypes = options.enabledNodeTypes
    ? new Set(options.enabledNodeTypes)
    : new Set(funnelNodeRegistry.map((entry) => entry.type));
  const allowedFirstNodeTypes = new Set(
    options.allowedFirstNodeTypes && options.allowedFirstNodeTypes.length > 0
      ? options.allowedFirstNodeTypes
      : funnelNodeRegistry
          .filter(
            (entry) => "allowedAsFirstNodeByDefault" in entry && entry.allowedAsFirstNodeByDefault,
          )
          .map((entry) => entry.type),
  );

  if (!definition.tenantId || !definition.organizationId) {
    issues.push({
      code: "FUNNEL_TENANT_CONTEXT_REQUIRED",
      severity: "error",
      message: "Funnel definitions require tenant and organization context.",
    });
  }

  if (definition.nodes.length === 0) {
    issues.push({
      code: "FUNNEL_NODE_MISSING",
      severity: "error",
      message: "Funnels require at least one node.",
    });
  }

  if (definition.nodes.length > maxNodes) {
    issues.push({
      code: "FUNNEL_NODE_LIMIT_EXCEEDED",
      severity: "error",
      message: `Funnel has ${definition.nodes.length} nodes, above the configured limit of ${maxNodes}.`,
    });
  }

  for (const node of definition.nodes) {
    if (nodeIds.has(node.id)) {
      issues.push({
        code: "FUNNEL_DUPLICATE_NODE_ID",
        severity: "error",
        message: `Duplicate funnel node id: ${node.id}.`,
        nodeId: node.id,
        nodeType: node.type,
      });
    }

    nodeIds.add(node.id);

    const registryEntry = funnelNodeRegistryByType.get(node.type);

    if (!registryEntry) {
      issues.push({
        code: "FUNNEL_NODE_UNSUPPORTED",
        severity: "error",
        message: `Unsupported funnel node type: ${node.type}.`,
        nodeId: node.id,
        nodeType: node.type,
      });
      continue;
    }

    if (!enabledTypes.has(node.type)) {
      issues.push({
        code: "FUNNEL_NODE_ENTITLEMENT_BLOCKED",
        severity: "error",
        message: `${registryEntry.label} is not available for the current platform configuration.`,
        nodeId: node.id,
        nodeType: node.type,
      });
    }

    for (const key of registryEntry.requiredConfig) {
      if (!hasRequiredConfig(node.config, key)) {
        issues.push({
          code: "FUNNEL_NODE_CONFIG_REQUIRED",
          severity: "error",
          message: `${registryEntry.label} requires configuration value: ${key}.`,
          nodeId: node.id,
          nodeType: node.type,
        });
      }
    }

    if (registryEntry.category === "agent" && !Array.isArray(node.config.knowledgebaseIds)) {
      issues.push({
        code: "FUNNEL_AI_CONTEXT_SUMMARY",
        severity: "warning",
        message:
          "AI agent node should declare selected knowledgebases; always-on organization context and contact memory remain automatic.",
        nodeId: node.id,
        nodeType: node.type,
      });
    }

    const providerType = providerConnectionTypeForFunnelNode(node.type);

    if (providerType) {
      const connectionRef = node.config["connectionRef"] ?? "system_default";
      const connectionId = node.config["connectionId"];

      if (connectionRef !== "system_default" && connectionRef !== "tenant_connection") {
        issues.push({
          code: "FUNNEL_PROVIDER_CONNECTION_REF_UNSUPPORTED",
          severity: "error",
          message: `${registryEntry.label} has an unsupported provider connection reference.`,
          nodeId: node.id,
          nodeType: node.type,
        });
      }

      if (connectionRef === "tenant_connection" && !isNonEmptyString(connectionId)) {
        issues.push({
          code: "FUNNEL_PROVIDER_CONNECTION_ID_REQUIRED",
          severity: "error",
          message: `${registryEntry.label} requires a saved ${providerType} connection when tenant connection routing is selected.`,
          nodeId: node.id,
          nodeType: node.type,
        });
      }
    }
  }

  const edgeIds = new Set<string>();

  for (const edge of definition.edges) {
    if (edgeIds.has(edge.id)) {
      issues.push({
        code: "FUNNEL_DUPLICATE_EDGE_ID",
        severity: "error",
        message: `Duplicate funnel edge id: ${edge.id}.`,
        edgeId: edge.id,
      });
    }

    edgeIds.add(edge.id);

    const sourceNode = definition.nodes.find((node) => node.id === edge.sourceNodeId);
    const targetNode = definition.nodes.find((node) => node.id === edge.targetNodeId);
    const sourceRegistry = sourceNode ? funnelNodeRegistryByType.get(sourceNode.type) : undefined;
    const targetRegistry = targetNode ? funnelNodeRegistryByType.get(targetNode.type) : undefined;

    if (!sourceNode || !targetNode) {
      issues.push({
        code: "FUNNEL_EDGE_UNKNOWN_NODE",
        severity: "error",
        message: `Funnel edge references an unknown node: ${edge.id}.`,
        edgeId: edge.id,
      });
      continue;
    }

    if (
      sourceRegistry &&
      sourceRegistry.outputPorts.length > 0 &&
      !sourceRegistry.outputPorts.some((port) => port.id === edge.sourcePortId)
    ) {
      issues.push({
        code: "FUNNEL_EDGE_UNKNOWN_PORT",
        severity: "error",
        message: `Funnel edge uses an unsupported output port: ${edge.sourcePortId}.`,
        edgeId: edge.id,
        nodeId: edge.sourceNodeId,
        nodeType: sourceNode.type,
      });
    }

    if (
      targetRegistry &&
      targetRegistry.inputPorts.length > 0 &&
      edge.targetPortId &&
      !targetRegistry.inputPorts.some((port) => port.id === edge.targetPortId)
    ) {
      issues.push({
        code: "FUNNEL_EDGE_UNKNOWN_TARGET_PORT",
        severity: "error",
        message: `Funnel edge uses an unsupported target port: ${edge.targetPortId}.`,
        edgeId: edge.id,
        nodeId: edge.targetNodeId,
        nodeType: targetNode.type,
      });
    }
  }

  const incomingByNode = new Map(definition.nodes.map((node) => [node.id, 0]));
  const outgoingBySourcePort = new Map<string, number>();

  for (const edge of definition.edges) {
    incomingByNode.set(edge.targetNodeId, (incomingByNode.get(edge.targetNodeId) ?? 0) + 1);
    const sourceNode = definition.nodes.find((node) => node.id === edge.sourceNodeId);
    const sourceRegistry = sourceNode ? funnelNodeRegistryByType.get(sourceNode.type) : undefined;
    const key = `${edge.sourceNodeId}:${edge.sourcePortId}`;

    outgoingBySourcePort.set(key, (outgoingBySourcePort.get(key) ?? 0) + 1);

    if (
      sourceRegistry &&
      !sourceRegistry.allowsMultipleOutgoingEdgesByPort &&
      (outgoingBySourcePort.get(key) ?? 0) > 1
    ) {
      issues.push({
        code: "FUNNEL_EDGE_DUPLICATE_SOURCE_PORT",
        severity: "error",
        message: `${sourceRegistry.label} can only route ${edge.sourcePortId} to one next node.`,
        edgeId: edge.id,
        nodeId: edge.sourceNodeId,
        nodeType: sourceNode?.type,
      });
    }
  }

  for (const node of definition.nodes.filter(
    (candidate) => (incomingByNode.get(candidate.id) ?? 0) === 0,
  )) {
    const registryEntry = funnelNodeRegistryByType.get(node.type);

    if (!allowedFirstNodeTypes.has(node.type) || !registryEntry?.allowedAsFirstNode) {
      issues.push({
        code: "FUNNEL_START_NODE_NOT_ALLOWED",
        severity: "error",
        message: `${registryEntry?.label ?? node.type} cannot be the first node in a funnel.`,
        nodeId: node.id,
        nodeType: node.type,
      });
    }
  }

  for (const node of definition.nodes.filter((candidate) => candidate.type === "wait")) {
    const incomingRoutes = definition.edges.filter((edge) => edge.targetNodeId === node.id);
    const waitRoutes = definition.edges.filter((edge) => edge.sourceNodeId === node.id);
    const waitRegistry = funnelNodeRegistryByType.get("wait");
    const selectedEvents = Array.isArray(node.config["eventTypes"])
      ? node.config["eventTypes"].filter((entry): entry is string => typeof entry === "string")
      : [];
    const maxWaitMs = parseDurationMs(node.config["fallbackMaxWait"] ?? node.config["maxWait"]);
    const activeDurationMs = campaignDurationMs(definition);
    const waitPortsByEvent = new Map(
      (waitRegistry?.outputPorts ?? [])
        .filter((port): port is FunnelPort & { event: string } => Boolean(port.event))
        .map((port) => [port.event, port]),
    );
    const routedEvents = new Set(
      waitRoutes
        .map((edge) => waitRegistry?.outputPorts.find((port) => port.id === edge.sourcePortId))
        .flatMap((port) => (port?.event ? [port.event] : [])),
    );

    if (selectedEvents.length === 0) {
      issues.push({
        code: "FUNNEL_WAIT_EVENT_REQUIRED",
        severity: "error",
        message: "Wait must select at least one event that can resume the funnel.",
        nodeId: node.id,
        nodeType: node.type,
      });
    }

    for (const eventType of selectedEvents) {
      if (!waitPortsByEvent.has(eventType)) {
        issues.push({
          code: "FUNNEL_WAIT_EVENT_UNSUPPORTED",
          severity: "error",
          message: `Wait cannot resume on unsupported event type: ${eventType}.`,
          nodeId: node.id,
          nodeType: node.type,
        });
        continue;
      }

      if (!routedEvents.has(eventType)) {
        issues.push({
          code: "FUNNEL_WAIT_EVENT_ROUTE_MISSING",
          severity: "error",
          message: `Wait event ${eventType} must route to a downstream node.`,
          nodeId: node.id,
          nodeType: node.type,
        });
      }

      const incomingSourceEvents = incomingRoutes
        .map((edge) => {
          const sourceNode = definition.nodes.find(
            (candidate) => candidate.id === edge.sourceNodeId,
          );
          const sourceRegistry = sourceNode
            ? funnelNodeRegistryByType.get(sourceNode.type)
            : undefined;

          return sourceRegistry?.outputPorts.find((port) => port.id === edge.sourcePortId)?.event;
        })
        .filter((event): event is string => typeof event === "string");

      const allowedByIncomingEvents =
        incomingSourceEvents.length === 0
          ? []
          : incomingSourceEvents.flatMap(
              (sourceEvent) => waitEventsBySourceEvent.get(sourceEvent) ?? [],
            );

      if (
        incomingSourceEvents.length > 0 &&
        allowedByIncomingEvents.length > 0 &&
        !allowedByIncomingEvents.includes(eventType)
      ) {
        issues.push({
          code: "FUNNEL_WAIT_EVENT_UPSTREAM_MISMATCH",
          severity: "error",
          message: `Wait event ${eventType} is not compatible with the upstream outcome.`,
          nodeId: node.id,
          nodeType: node.type,
        });
      }

      const compatibleTargets = waitConsumersByEvent.get(eventType) ?? [];
      const matchingRoutes = waitRoutes.filter((edge) => {
        const port = waitRegistry?.outputPorts.find(
          (candidate) => candidate.id === edge.sourcePortId,
        );

        return port?.event === eventType;
      });

      for (const route of matchingRoutes) {
        const targetNode = definition.nodes.find(
          (candidate) => candidate.id === route.targetNodeId,
        );

        if (
          targetNode &&
          compatibleTargets.length > 0 &&
          !compatibleTargets.includes(targetNode.type)
        ) {
          issues.push({
            code: "FUNNEL_WAIT_EVENT_TARGET_MISMATCH",
            severity: "error",
            message: `Wait event ${eventType} cannot route directly to ${targetNode.type}.`,
            edgeId: route.id,
            nodeId: node.id,
            nodeType: node.type,
          });
        }
      }
    }

    if (maxWaitMs !== undefined && activeDurationMs !== undefined && maxWaitMs > activeDurationMs) {
      issues.push({
        code: "FUNNEL_WAIT_MAX_EXCEEDS_CAMPAIGN_DURATION",
        severity: "error",
        message: "Wait fallback maximum cannot exceed the campaign active duration.",
        nodeId: node.id,
        nodeType: node.type,
      });
    }

    if (waitRoutes.length === 0) {
      issues.push({
        code: "FUNNEL_WAIT_ROUTE_MISSING",
        severity: "warning",
        message: "Wait should route at least one received event to a downstream node.",
        nodeId: node.id,
        nodeType: node.type,
      });
    }
  }

  for (const node of definition.nodes.filter((candidate) => candidate.type === "contact_update")) {
    const updates = Array.isArray(node.config["updates"]) ? node.config["updates"] : [];

    if (updates.length === 0) {
      issues.push({
        code: "FUNNEL_CONTACT_UPDATE_REQUIRED",
        severity: "error",
        message: "Contact Update requires at least one guided update.",
        nodeId: node.id,
        nodeType: node.type,
      });
    }

    for (const update of updates) {
      if (!update || typeof update !== "object" || Array.isArray(update)) {
        issues.push({
          code: "FUNNEL_CONTACT_UPDATE_INVALID",
          severity: "error",
          message: "Contact Update entries must be objects.",
          nodeId: node.id,
          nodeType: node.type,
        });
        continue;
      }

      const field = (update as Record<string, unknown>)["field"];
      const value = (update as Record<string, unknown>)["value"];

      if (
        typeof field !== "string" ||
        (!allowedContactUpdateFields.has(field) && !field.startsWith("custom."))
      ) {
        issues.push({
          code: "FUNNEL_CONTACT_UPDATE_FIELD_UNSUPPORTED",
          severity: "error",
          message: "Contact Update field must be a known contact field or a mapped custom field.",
          nodeId: node.id,
          nodeType: node.type,
        });
      }

      if (
        field === "status" &&
        (typeof value !== "string" || !allowedContactStatusValues.has(value))
      ) {
        issues.push({
          code: "FUNNEL_CONTACT_UPDATE_STATUS_UNSUPPORTED",
          severity: "error",
          message: "Contact Update status must be one of the supported contact statuses.",
          nodeId: node.id,
          nodeType: node.type,
        });
      }
    }
  }

  if (definition.nodes.length > 0 && !definition.nodes.some((node) => node.type === "stop")) {
    issues.push({
      code: "FUNNEL_TERMINAL_NODE_MISSING",
      severity: "warning",
      message: "Funnels should include at least one stop/end node with an explicit outcome.",
    });
  }

  if (funnelHasCycle(definition)) {
    issues.push({
      code: "FUNNEL_LOOP_UNSUPPORTED",
      severity: "error",
      message: "Funnels cannot publish with loops until loop controls and execution limits exist.",
    });
  }

  const reachable = reachableFunnelNodeIds(definition);

  for (const node of definition.nodes) {
    if (!reachable.has(node.id)) {
      issues.push({
        code: "FUNNEL_NODE_UNREACHABLE",
        severity: "warning",
        message: "Node is not reachable from the funnel entry path.",
        nodeId: node.id,
        nodeType: node.type,
      });
    }
  }

  return {
    valid: issues.every((issue) => issue.severity !== "error"),
    issues,
  };
}

export function createFunnelDefinitionRecord(input: {
  definition: FunnelDefinition;
  status?: FunnelStatus;
  createdByUserId?: string;
  createdAt?: string;
  updatedAt?: string;
  activeVersionId?: string;
}): FunnelDefinitionRecord {
  const createdAt = input.createdAt ?? nowIso();

  if (!input.definition.tenantId || !input.definition.organizationId) {
    throw new Error("Funnel records require tenant and organization context.");
  }

  return {
    id: input.definition.id,
    tenantId: input.definition.tenantId,
    organizationId: input.definition.organizationId,
    agencyId: input.definition.agencyId,
    campaignId: input.definition.campaignId,
    name: input.definition.name,
    status: input.status ?? "draft",
    activeVersionId: input.activeVersionId,
    activeVersion: input.activeVersionId ? input.definition.version : undefined,
    createdByUserId: input.createdByUserId,
    createdAt,
    updatedAt: input.updatedAt ?? createdAt,
  };
}

export function createFunnelVersionRecord(input: {
  definition: FunnelDefinition;
  status?: FunnelStatus;
  id?: string;
  validation?: FunnelValidationResult;
  createdAt?: string;
  publishedAt?: string;
}): FunnelVersionRecord {
  return {
    id: input.id ?? createId("funnel_version"),
    funnelId: input.definition.id,
    tenantId: input.definition.tenantId,
    organizationId: input.definition.organizationId,
    agencyId: input.definition.agencyId,
    version: input.definition.version,
    definition: input.definition,
    status: input.status ?? "draft",
    validation: input.validation ?? validateFunnelDefinition(input.definition),
    publishedAt: input.publishedAt,
    createdAt: input.createdAt ?? nowIso(),
  };
}

export function createFunnelWorkflowDefinition(input: {
  definition: FunnelDefinition;
}): WorkflowDefinition {
  const triggerNode: WorkflowNodeDefinition = {
    id: "trigger_funnel_start",
    kind: "trigger",
    name: "Funnel start",
    config: {
      funnelId: input.definition.id,
      campaignId: input.definition.campaignId,
    },
  };

  return {
    id: input.definition.id,
    tenantId: input.definition.tenantId,
    name: input.definition.name,
    version: input.definition.version,
    nodes: [
      triggerNode,
      ...input.definition.nodes.map((node) => ({
        id: node.id,
        kind: funnelNodeWorkflowKind(node.type),
        name: node.label,
        config: {
          ...node.config,
          funnelNodeType: node.type,
          executionHandlerKey: funnelNodeRegistryByType.get(node.type)?.executionHandlerKey,
        },
      })),
    ],
    connections: [
      ...(input.definition.nodes[0]
        ? [
            {
              id: "edge_funnel_start",
              sourceNodeId: triggerNode.id,
              targetNodeId: input.definition.nodes[0].id,
              label: "Start",
            },
          ]
        : []),
      ...input.definition.edges.map((edge) => ({
        id: edge.id,
        sourceNodeId: edge.sourceNodeId,
        targetNodeId: edge.targetNodeId,
        label: edge.label,
        condition: {
          ...edge.condition,
          sourcePortId: edge.sourcePortId,
          targetPortId: edge.targetPortId,
        },
      })),
    ],
  };
}

export function createFunnelRunRequest(input: {
  definition: FunnelDefinition;
  triggerType?: WorkflowTriggerType;
  traceId: string;
  payload?: Record<string, unknown>;
  executionId?: string;
}): WorkflowRunRequest {
  const validation = validateFunnelDefinition(input.definition);

  if (!validation.valid) {
    throw new Error("Cannot create a funnel run request for an invalid funnel definition.");
  }

  return createWorkflowRunRequest({
    definition: createFunnelWorkflowDefinition({ definition: input.definition }),
    triggerType: input.triggerType ?? "manual",
    traceId: input.traceId,
    input: {
      ...(input.payload ?? {}),
      funnelId: input.definition.id,
      campaignId:
        typeof input.payload?.["campaignId"] === "string"
          ? input.payload["campaignId"]
          : input.definition.campaignId,
    },
    executionId: input.executionId,
  });
}

export function createFunnelExternalEventRoute(input: {
  funnelId: string;
  nodeId: string;
  eventName: string;
  routeId?: string;
  token?: string;
  basePath?: string;
  createdAt?: string;
}): FunnelExternalEventRoute {
  const routeId = input.routeId ?? createId("funnel_event");
  const token = input.token ?? createId("funnel_token");
  const basePath = input.basePath ?? "/api/v1/funnels/external-events";

  return {
    routeId,
    funnelId: input.funnelId,
    nodeId: input.nodeId,
    eventName: input.eventName,
    webhookPath: `${basePath}/${routeId}`,
    token,
    createdAt: input.createdAt ?? nowIso(),
  };
}

export function normalizeFunnelExternalEventPayload(value: unknown): FunnelExternalEventPayload {
  const record =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const metadata = record.metadata;

  return {
    eventName: isNonEmptyString(record.eventName) ? record.eventName : "external_event.received",
    contactId: isNonEmptyString(record.contactId) ? record.contactId : undefined,
    email: isNonEmptyString(record.email) ? record.email : undefined,
    phone: isNonEmptyString(record.phone) ? record.phone : undefined,
    campaignId: isNonEmptyString(record.campaignId) ? record.campaignId : undefined,
    touchpointId: isNonEmptyString(record.touchpointId) ? record.touchpointId : undefined,
    status: isNonEmptyString(record.status) ? record.status : undefined,
    externalEventId: isNonEmptyString(record.externalEventId) ? record.externalEventId : undefined,
    occurredAt: isNonEmptyString(record.occurredAt) ? record.occurredAt : nowIso(),
    metadata:
      metadata && typeof metadata === "object" && !Array.isArray(metadata)
        ? (metadata as Record<string, unknown>)
        : {},
  };
}

function redactedPayload(payload: Record<string, unknown>): {
  payload: Record<string, unknown>;
  redaction: Record<string, unknown>;
} {
  const redactedPaths: string[] = [];
  const sensitiveKeyMarkers = [
    "authorization",
    "auth",
    "token",
    "secret",
    "password",
    "passwd",
    "apikey",
    "cookie",
    "signature",
    "credential",
    "webhookauthorization",
  ];

  function redact(value: unknown, path: string): unknown {
    if (Array.isArray(value)) {
      return value.map((entry, index) => redact(entry, `${path}[${index}]`));
    }

    if (!value || typeof value !== "object") {
      return value;
    }

    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => {
        const nextPath = path ? `${path}.${key}` : key;

        const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");

        if (sensitiveKeyMarkers.some((marker) => normalizedKey.includes(marker))) {
          redactedPaths.push(nextPath);

          return [key, "[redacted]"];
        }

        return [key, redact(entry, nextPath)];
      }),
    );
  }

  const nextPayload = redact(payload, "") as Record<string, unknown>;

  return {
    payload: nextPayload,
    redaction: redactedPaths.length > 0 ? { redactedPaths } : {},
  };
}

export function createFunnelSessionRecord(input: {
  tenantId: string;
  organizationId: string;
  agencyId?: string;
  campaignId?: string;
  contactId?: string;
  funnelId?: string;
  funnelVersionId?: string;
  executionId?: string;
  source?: FunnelSessionSource | string;
  status?: FunnelSessionStatus;
  currentNodeKey?: string;
  startedAt?: string;
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}): FunnelSessionRecord {
  assertTenantInput({ tenantId: input.tenantId, organizationId: input.organizationId });
  const now = nowIso();

  return {
    id: createId("funnel_session"),
    tenantId: input.tenantId,
    organizationId: input.organizationId,
    agencyId: input.agencyId,
    campaignId: input.campaignId,
    contactId: input.contactId,
    funnelId: input.funnelId,
    funnelVersionId: input.funnelVersionId,
    executionId: input.executionId,
    status: input.status ?? "started",
    source: input.source ?? "manual",
    currentNodeKey: input.currentNodeKey,
    startedAt: input.startedAt ?? now,
    expiresAt: input.expiresAt,
    createdAt: now,
    updatedAt: now,
    metadata: input.metadata ?? {},
  };
}

export function createFunnelSessionEventRecord(input: {
  session: FunnelSessionRecord;
  eventType: string;
  status?: string;
  payload?: Record<string, unknown>;
  occurredAt?: string;
}): FunnelSessionEventRecord {
  if (!input.eventType.trim()) {
    throw new Error("Funnel session events require an event type.");
  }

  return {
    id: createId("funnel_session_event"),
    sessionId: input.session.id,
    tenantId: input.session.tenantId,
    organizationId: input.session.organizationId,
    agencyId: input.session.agencyId,
    eventType: input.eventType,
    status: input.status,
    payload: redactedPayload(input.payload ?? {}).payload,
    occurredAt: input.occurredAt ?? nowIso(),
  };
}

export function createFunnelNodeEventRecord(input: {
  session: FunnelSessionRecord;
  nodeKey: string;
  nodeType: string;
  eventType: string;
  status?: string;
  inputPayload?: Record<string, unknown>;
  outputPayload?: Record<string, unknown>;
  errorCode?: string;
  errorMessage?: string;
  providerEventId?: string;
  occurredAt?: string;
}): FunnelNodeEventRecord {
  if (!input.nodeKey || !input.nodeType || !input.eventType) {
    throw new Error("Funnel node events require node and event context.");
  }

  const inputRedaction = redactedPayload(input.inputPayload ?? {});
  const outputRedaction = redactedPayload(input.outputPayload ?? {});

  return {
    id: createId("funnel_node_event"),
    sessionId: input.session.id,
    tenantId: input.session.tenantId,
    organizationId: input.session.organizationId,
    agencyId: input.session.agencyId,
    nodeKey: input.nodeKey,
    nodeType: input.nodeType,
    eventType: input.eventType,
    status: input.status ?? "recorded",
    inputPayload: inputRedaction.payload,
    outputPayload: outputRedaction.payload,
    redaction: {
      input: inputRedaction.redaction,
      output: outputRedaction.redaction,
    },
    errorCode: input.errorCode,
    errorMessage: input.errorMessage,
    providerEventId: input.providerEventId,
    occurredAt: input.occurredAt ?? nowIso(),
  };
}

export function createFunnelWaitStateRecord(input: {
  session: FunnelSessionRecord;
  sourceNodeKey: string;
  awaitedEventTypes: string[];
  nextRouteHints?: Record<string, unknown>;
  expiresAt?: string;
}): FunnelWaitStateRecord {
  if (!input.sourceNodeKey) {
    throw new Error("Funnel wait states require a source node.");
  }

  const now = nowIso();

  return {
    id: createId("funnel_wait"),
    sessionId: input.session.id,
    tenantId: input.session.tenantId,
    organizationId: input.session.organizationId,
    agencyId: input.session.agencyId,
    sourceNodeKey: input.sourceNodeKey,
    awaitedEventTypes: [...new Set(input.awaitedEventTypes.filter(Boolean))],
    status: "waiting",
    nextRouteHints: input.nextRouteHints ?? {},
    expiresAt: input.expiresAt,
    createdAt: now,
    updatedAt: now,
  };
}
